import React, { ChangeEvent, useMemo, useState } from 'react'
import ReactCountryFlag from 'react-country-flag'
import { generatePath } from 'react-router-dom'
import { CacheLabel } from '@/components/CacheLabel'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormFieldLayout,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ClientDetailsMoreMenu } from '@/routes/Records/Clients/Details/ClientDetailsDashboard/ClientDetailsMoreMenu'
import { ClientStatusId } from '@/utils/clients.helpers'
import { BusinessNature } from 'api/Clients/Types/BusinessNature'
import { getAMLScore as getAMLScoreData } from 'api/Clients/Types/ClientAmlRiskLevelType'
import {
  ClientDetailedDto,
  ClientLeadTeamDto,
  ClientNationalityDto,
  ClientPlatformLimitDto,
  ClientProductStatesDto,
  UpdateClientPlatformLimitDto,
} from 'api/Clients/Types/ClientDetailedDto'
import { ClientType } from 'api/Clients/Types/ClientType'
import { POIDocumentType } from 'api/Clients/Types/DocumentType'
import { EduLevel } from 'api/Clients/Types/EduLevel'
import { LandingPageDto } from 'api/Clients/Types/LandingPageDto'
import { Language } from 'api/Clients/Types/Language'
import { NciType } from 'api/Clients/Types/NciType'
import { SalesStatus } from 'api/Clients/Types/SalesStatus'
import { TickmillCompany } from 'api/Clients/Types/TickmillCompany'
import { TickmillProductDto } from 'api/Clients/Types/TickmillProductDto'
import { TruliooStatus } from 'api/Clients/Types/TruliooStatus'
import { UpdateClientStatus } from 'api/Clients/Types/UpdateClientStatus'
import { WorldCheckStatus } from 'api/Clients/Types/WorldCheckStatus'
import { NameDto } from 'api/Common/Types'
import { DocumentRequestType } from 'api/Documents/Types/DocumentRequests'
import { CountryDto } from 'api/Identity/Types/Country'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { TeamDto } from 'api/Identity/Types/Team'
import { isUserStatusLocked, SimpleUserResponse } from 'api/Identity/Types/UserResponse'
import { UserStatisticsDto } from 'api/Identity/Types/UserStatisticsDto'
import { BookType } from 'api/Trading-Accounts/Types/BookType'
import { IntroducingBrokerBasicDto } from 'api/Trading-Accounts/Types/IntroducingBrokerBasicDto'
import { LeverageType } from 'api/Trading-Accounts/Types/LeverageType'
import { PlatformTypeId, PlatformTypeName } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import {
  TradingViewPlanDto,
  TradingViewPlanExpirationDaysDto,
} from 'api/Trading-Accounts/Types/TradingViewPlanDto'
import AddButton from 'components/AddButton/AddButton'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { WarningTwoTone } from 'components/Icon'
import MoreButton from 'components/MoreButton/MoreButton'
import { Select } from 'components/Select'
import { FormikProps, withFormik } from 'formik'
import { useCountriesToCountryCodes } from 'hooks/useCountriesToCountryCodes'
import countryConverter from 'i18n-iso-countries'
import { CheckCheck, ChevronDown, CircleAlert, HatGlasses, Info } from 'lucide-react'
import { MainIBSchemeEnum } from 'types/MainIBSchemeEnum'
import { INTRODUCING_BROKERS_CACHE_MESSAGE } from 'utils/cache'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { getPrimary } from 'utils/getPrimary'
import { hasPermission } from 'utils/permissionsValidator'

import { uniqueLeverageTypes } from '../../../TradingAccounts/Details/TradingAccountDetailsForm'
import { ClientNavigation, ClientTab } from '../../Navigation/ClientNavigation'
import { FormFieldIconBlock } from '../Client.styles'
import { ClientDetailsDashboardAddEditNationalityTeam } from './ClientDetailsDashboardAddEditNationalityTeam'
import { ClientDetailsDashboardAssignTradingViewPlan } from './ClientDetailsDashboardAssignTradingViewPlan'
import { ClientDetailsDashboardEditPlatformLimits } from './ClientDetailsDashboardEditPlatformLimits'

export interface ModalData {
  type: 'nationality' | 'teams'
  editId?: string | null
  data: ClientNationalityDto | ClientLeadTeamDto
}

interface ModalProps {
  visible: boolean
  request?: string
  action?: () => void
}

const ClientDetailsDashboardFormUI: React.FC<
  FormikProps<ClientDetailsFormValues> & ClientDetailsDashboardFormProps
> = (props) => {
  const {
    setFieldValue,
    values,
    data,
    permissions,
    resetForm,
    submitForm,
    userStatistics,
    teams,
    documentType,
    businessNature,
    countries,
    gender,
    dateFormatType,
    users,
    salesStatuses,
    truliooEidStatuses,
    truliooDocVStatuses,
    worldCheckStatuses,
    truliooWatchlistStatuses,
    introducingBrokersByCompany,
    complianceVulnerabilityGroups,
    bookType,
    accountLimits,
    updatePlatformLimits,
    tradingViewPlans,
    tradingViewPlanExpirationDays,
    leverageType,
    classification,
    marketDataClassification,
    language,
    nciType,
    education,
    preferredCurrency,
    displayCurrency,
    landingPages,
    setAmlDetailsModal,
    verifyClient,
    taxIdAvailabilityStatus,
    amlCategories,
  } = props

  const [isEdit, setEdit] = useState<boolean>(false)
  const [isModalData, setModalData] = useState<ModalData>()
  const [platformLimitsModal, setPlatformLimitsModal] = useState<ClientPlatformLimitDto>()
  const [tradingViewPlanModal, setTradingViewPlanModal] = useState<ClientPlatformLimitDto>()
  const [productModal, setProductModal] = useState<number | null>()
  const [documentRequestModal, setDocumentRequestModal] = useState<ModalProps>({
    visible: false,
  })

  const uniqueCountryCodes = useCountriesToCountryCodes(countries)
  const uniqueCountryCodeOptions = useMemo(
    () =>
      uniqueCountryCodes.map((countryCode) => ({
        value: countryCode,
        label: countryCode,
      })),
    [uniqueCountryCodes]
  )
  const assigneeOptions = useMemo(
    () =>
      users.map((user) => ({
        id: user.id,
        name: user.name,
        rightAddon: isUserStatusLocked(user?.status?.id) && (
          <Tooltip>
            <TooltipTrigger render={<span className='inline-flex' />}>
              <CircleAlert aria-label='User Locked' className='text-destructive' />
            </TooltipTrigger>
            <TooltipContent side='top'>User Locked</TooltipContent>
          </Tooltip>
        ),
      })),
    [users]
  )
  const displayCurrencyOptions = useMemo(
    () => displayCurrency.map((currency) => ({ ...currency, name: currency.id })),
    [displayCurrency]
  )
  const preferredCurrencyOptions = useMemo(
    () => preferredCurrency.map((currency) => ({ ...currency, name: currency.id })),
    [preferredCurrency]
  )
  const countryOptions = useMemo(
    () => countries.map((country) => ({ id: country.id, name: country.name })),
    [countries]
  )
  const leverageOptions = useMemo(
    () => uniqueLeverageTypes(leverageType.concat(values.maxLeverage || [])),
    [leverageType, values.maxLeverage]
  )

  const handleCancel = () => {
    setEdit(false)
    resetForm()
  }

  const saveModalForm = () => {
    if (isModalData && isModalData.type === 'nationality' && isModalData.data.id) {
      let nationalities = values.nationalities
      if (isModalData.data.isPrimary) {
        nationalities = values.nationalities.map((x) => ({ ...x, isPrimary: false }))
      }
      if (isModalData.editId) {
        nationalities = nationalities.filter((x) => x.id !== isModalData.editId)
      }
      let updated = [
        ...nationalities.filter((x) => x.id !== isModalData.data.id),
        isModalData.data as ClientNationalityDto,
      ]
      if (updated.length > 0 && !updated.some((x) => x.isPrimary)) {
        updated = updated.map((x, idx) => ({ ...x, isPrimary: idx === 0 }))
      }
      setFieldValue('nationalities', updated)
    }

    if (isModalData && isModalData.type === 'teams' && isModalData.data.id) {
      let teamDtos = values.teams
      if (isModalData.data.isPrimary) {
        teamDtos = values.teams.map((x) => ({ ...x, isPrimary: false }))
      }
      if (isModalData.editId) {
        teamDtos = teamDtos.filter((x) => x.id !== isModalData.editId)
      }
      setFieldValue('teams', [
        ...teamDtos.filter((x) => x.id !== isModalData.data.id),
        isModalData.data as ClientLeadTeamDto,
      ])
    }
    setModalData(undefined)
  }

  const renderTeams = () => {
    if (!values.teams) return []
    return values.teams.map((team) => {
      return (
        <TableRow key={team.id}>
          <TableCell>{team.name}</TableCell>
          <TableCell className='w-24'>
            <Checkbox checked={team.isPrimary} disabled />
          </TableCell>
          <TableCell className='w-16 text-right'>
            <MoreButton
              disabled={!isEdit}
              items={[
                {
                  name: 'Edit',
                  onClick: () => setModalData({ type: 'teams', data: team, editId: team.id }),
                },
                {
                  name: 'Delete',
                  onClick: () =>
                    props.setFieldValue(
                      'teams',
                      values.teams.filter((x) => x.id !== team.id)
                    ),
                },
              ]}
            />
          </TableCell>
        </TableRow>
      )
    })
  }

  const renderNationalities = () => {
    if (!values.nationalities) return []

    return values.nationalities.map((clientNationality: ClientNationalityDto) => {
      return (
        <TableRow key={clientNationality.id}>
          <TableCell>{clientNationality.name}</TableCell>
          <TableCell align='center' className='w-24'>
            <Checkbox checked={clientNationality.isPrimary} disabled />
          </TableCell>
          <TableCell className='w-16 text-right'>
            <MoreButton
              disabled={!isEdit}
              items={[
                {
                  name: 'Edit',
                  onClick: () =>
                    setModalData({
                      type: 'nationality',
                      data: clientNationality,
                      editId: clientNationality.id,
                    }),
                },
                {
                  name: 'Delete',
                  disabled: values.nationalities.length <= 1,
                  onClick: () => {
                    const filtered = values.nationalities.filter(
                      (x) => x.id !== clientNationality.id
                    )
                    if (!filtered.length) return
                    if (!filtered.some((x) => x.isPrimary)) {
                      filtered[0] = { ...filtered[0], isPrimary: true }
                    }
                    props.setFieldValue('nationalities', filtered)
                  },
                },
              ]}
            />
          </TableCell>
        </TableRow>
      )
    })
  }

  const getTradingViewPlanLabel = (platformLimit: ClientPlatformLimitDto) => {
    if (platformLimit.platformTypeId !== PlatformTypeId.TickmillTrader) {
      return 'NA'
    }
    const planName = platformLimit.tradingViewPlan?.name
    if (planName) {
      return planName
    }
    const planId =
      platformLimit.tradingViewPlan?.id ||
      platformLimit.tradingViewCouponParameters?.tradingViewPlanId
    if (planId === null || planId === undefined) {
      return 'NA'
    }
    const plan = tradingViewPlans.find((p) => p.id === planId)
    return plan?.name ?? planId.toString()
  }

  const renderPlatformLimits = () => {
    if (!accountLimits.length) return null
    return accountLimits.map((platformLimit) => (
      <TableRow key={platformLimit.platformTypeId}>
        <TableCell>{PlatformTypeName[platformLimit.platformTypeId]}</TableCell>
        <TableCell>{platformLimit.liveTradingAccountLimit}</TableCell>
        <TableCell>{platformLimit.demoTradingAccountLimit}</TableCell>
        <TableCell>{getTradingViewPlanLabel(platformLimit)}</TableCell>
        <TableCell className='w-16 text-center'>
          <MoreButton
            // EXCEPTION: This is needed by Petros and Business to be a separate locking than Edit->Client
            // due to Permissions on Edit client but ability to edit platforms
            // https://tickmill.atlassian.net/browse/INF-1552
            // disabled={!isEdit}
            items={[
              {
                name: 'Edit',
                onClick: () => setPlatformLimitsModal(platformLimit),
              },
              ...(platformLimit.platformTypeId === PlatformTypeId.TickmillTrader
                ? [
                    {
                      name: 'Assign TV Plan',
                      onClick: () => setTradingViewPlanModal(platformLimit),
                    },
                  ]
                : []),
            ]}
          />
        </TableCell>
      </TableRow>
    ))
  }

  const selectPhoneNumber = useMemo(() => {
    const [primaryPhoneNumber] = values.phoneNumbers.filter((x) => x.isPrimary)
    const [phoneNumber] = values.phoneNumbers
    return primaryPhoneNumber || phoneNumber
  }, [values.phoneNumbers])

  const updatePrimaryPhoneNumber = (
    updates: Partial<ClientDetailsFormValues['phoneNumbers'][number]>
  ) => {
    if (!selectPhoneNumber) return
    setFieldValue('phoneNumbers', [
      ...values.phoneNumbers.filter((x) => x.id !== selectPhoneNumber.id),
      { ...selectPhoneNumber, ...updates },
    ])
  }

  const updatePrimaryEmail = (address: string) => {
    const primaryEmail = getPrimary(values.emails)
    if (!primaryEmail) return

    setFieldValue('emails', [
      ...values.emails.filter((x) => x.id !== primaryEmail.id),
      { ...primaryEmail, address },
    ])
  }

  const updatePrimaryAddress = (updates: Partial<ClientDetailsFormValues['addresses'][number]>) => {
    const primaryAddress = getPrimary(values.addresses)
    if (!primaryAddress) return

    setFieldValue('addresses', [
      ...values.addresses.filter((x) => x.id !== primaryAddress.id),
      { ...primaryAddress, ...updates },
    ])
  }

  const countryFlag = useMemo(() => {
    if (!selectPhoneNumber?.countryCode) return
    // eslint-disable-next-line array-callback-return
    const phoneCountryData = countries.filter((country) => {
      if (country.countryCodeNumber)
        return (
          country.id !== 'GAZ' && country.countryCodeNumber.includes(selectPhoneNumber.countryCode)
        )
    })
    if (phoneCountryData.length < 1) return
    return phoneCountryData.map((countryData, index) => (
      <React.Fragment key={countryData.id}>
        {index > 0 && <span>/</span>}
        <ReactCountryFlag
          svg
          style={{
            height: 'auto',
            width: '22px',
          }}
          className='rounded-sm'
          countryCode={countryConverter.alpha3ToAlpha2(countryData.id) ?? 'US'}
          title={countryData.name}
        />
      </React.Fragment>
    ))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.phoneNumbers, selectPhoneNumber?.countryCode])

  const mapPlatformLimitForPayload = (
    platformLimit: ClientPlatformLimitDto
  ): Omit<
    ClientPlatformLimitDto,
    'tradingViewPlan' | 'tradingViewPlanExpirationDays' | 'tradingViewPlanAssignmentDate'
  > => {
    const isTradingViewPlatform = platformLimit.platformTypeId === PlatformTypeId.TickmillTrader

    return {
      platformTypeId: platformLimit.platformTypeId,
      liveTradingAccountLimit: platformLimit.liveTradingAccountLimit,
      demoTradingAccountLimit: platformLimit.demoTradingAccountLimit,
      tradingViewCouponParameters: isTradingViewPlatform
        ? {
            tradingViewPlanId:
              platformLimit.tradingViewCouponParameters?.tradingViewPlanId ??
              platformLimit.tradingViewPlan?.id ??
              0,
            tradingViewPlanExpirationDaysId:
              (platformLimit.tradingViewCouponParameters?.tradingViewPlanId ??
                platformLimit.tradingViewPlan?.id) === 0
                ? null
                : (platformLimit.tradingViewCouponParameters?.tradingViewPlanExpirationDaysId ??
                  platformLimit.tradingViewPlanExpirationDays?.id ??
                  null),
          }
        : { tradingViewPlanId: null, tradingViewPlanExpirationDaysId: null },
    }
  }

  const handlePlatformLimitsUpdate = (data: ClientPlatformLimitDto) => {
    const normalizedPlatformLimits = accountLimits.map((x) => {
      if (x.platformTypeId !== data.platformTypeId) {
        return mapPlatformLimitForPayload(x)
      }

      const tradingViewPlanId = data.tradingViewCouponParameters?.tradingViewPlanId ?? null
      const tradingViewPlanExpirationDaysId =
        tradingViewPlanId === 0
          ? null
          : (data.tradingViewCouponParameters?.tradingViewPlanExpirationDaysId ?? null)

      const tradingViewCouponParameters =
        x.platformTypeId === PlatformTypeId.TickmillTrader
          ? {
              tradingViewPlanId,
              tradingViewPlanExpirationDaysId,
            }
          : x.tradingViewCouponParameters

      return mapPlatformLimitForPayload({
        ...x,
        liveTradingAccountLimit: data.liveTradingAccountLimit,
        demoTradingAccountLimit: data.demoTradingAccountLimit,
        tradingViewCouponParameters,
      })
    })

    updatePlatformLimits({ liveAndDemoMaxTradingAccounts: normalizedPlatformLimits })
    setPlatformLimitsModal(undefined)
  }

  const handleTradingViewPlanUpdate = (data: ClientPlatformLimitDto) => {
    const tradingViewPlanId = data.tradingViewCouponParameters?.tradingViewPlanId ?? null
    const tradingViewPlanExpirationDaysId =
      tradingViewPlanId === 0
        ? null
        : (data.tradingViewCouponParameters?.tradingViewPlanExpirationDaysId ?? null)

    const normalizedPlatformLimits = accountLimits.map((x) =>
      mapPlatformLimitForPayload(
        x.platformTypeId === data.platformTypeId
          ? {
              ...x,
              tradingViewCouponParameters: {
                tradingViewPlanId,
                tradingViewPlanExpirationDaysId,
              },
            }
          : x
      )
    )

    updatePlatformLimits({ liveAndDemoMaxTradingAccounts: normalizedPlatformLimits })
    setTradingViewPlanModal(undefined)
  }

  const getAMLScore = useMemo(() => {
    return getAMLScoreData(amlCategories)(props.data.clientAmlSummary?.score)
  }, [props.data.clientAmlSummary, amlCategories])

  return (
    <React.Fragment>
      {documentRequestModal.visible && documentRequestModal.action && (
        <ConfirmationModal
          contentText={`Are you sure you wish to send the following request: ${documentRequestModal.request}?`}
          yesButtonText='Yes, Proceed'
          onYes={documentRequestModal.action}
          onCancel={() => setDocumentRequestModal({ visible: false })}
          visible={documentRequestModal.visible}
        />
      )}
      <ClientNavigation
        isEdit={isEdit}
        data={data}
        tab={ClientTab.INDIVIDUAL}
        updateClientStatus={verifyClient}
      />
      {isModalData && (
        <ClientDetailsDashboardAddEditNationalityTeam
          isOpen={!!isModalData}
          onClose={() => setModalData(undefined)}
          data={values as ClientDetailedDto}
          teams={teams}
          setModalData={setModalData}
          disabled={!isEdit}
          countries={countries}
          modalData={isModalData}
          saveModalForm={saveModalForm}
        />
      )}
      {platformLimitsModal && (
        <ClientDetailsDashboardEditPlatformLimits
          isOpen={!!platformLimitsModal}
          onClose={() => setPlatformLimitsModal(undefined)}
          modalData={platformLimitsModal}
          saveModalForm={handlePlatformLimitsUpdate}
        />
      )}
      {tradingViewPlanModal && (
        <ClientDetailsDashboardAssignTradingViewPlan
          isOpen={!!tradingViewPlanModal}
          onClose={() => setTradingViewPlanModal(undefined)}
          modalData={tradingViewPlanModal}
          tradingViewPlans={tradingViewPlans}
          tradingViewPlanExpirationDays={tradingViewPlanExpirationDays}
          saveModalForm={handleTradingViewPlanUpdate}
        />
      )}
      {productModal && (
        <Dialog open={!!productModal} onOpenChange={(open) => !open && setProductModal(null)}>
          <DialogContent size='lg'>
            <DialogHeader>
              <DialogTitle>
                {
                  values.clientProductStates.find((x) => x.tickmillProduct.id === productModal)
                    ?.tickmillProduct.name
                }{' '}
                Status
              </DialogTitle>
            </DialogHeader>
            <ProductDetails
              productState={values.clientProductStates?.find(
                (x) => x.tickmillProduct.id === productModal
              )}
            />
            <DialogFooter>
              <DialogClose render={<Button variant='outline' />}>Close</DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <DataGrid
        rightAddon={
          <ButtonGroup>
            {hasPermission(permissions, PermissionsEnum.CanImpersonate) && (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      size='icon'
                      variant='outline'
                      aria-label='CA Login'
                      disabled={props.isLoading}
                      onClick={() => props.onCALogin()}
                      data-test='ca-login-icon-button'
                    />
                  }
                >
                  <HatGlasses className='h-4 w-4' />
                </TooltipTrigger>
                <TooltipContent>Impersonate (CA Login)</TooltipContent>
              </Tooltip>
            )}
            {isEdit ? (
              <>
                <Button
                  disabled={props.isLoading}
                  variant='outline'
                  onClick={handleCancel}
                  data-test='cancel-button'
                >
                  Cancel
                </Button>
                <Button disabled={props.isLoading} onClick={submitForm} data-test='save-button'>
                  Save
                  {props.isLoading && <Spinner data-icon='inline-start' />}
                </Button>
              </>
            ) : (
              <Button
                disabled={
                  !hasPermission(permissions, PermissionsEnum.CanEditClient) || props.isLoading
                }
                onClick={() => setEdit(true)}
                data-test='edit-button'
              >
                Edit
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    type='button'
                    size='icon'
                    variant={'outline'}
                    aria-label='More options'
                    disabled={props.isLoading}
                  />
                }
              >
                <ChevronDown />
              </DropdownMenuTrigger>

              <DropdownMenuContent className='min-w-max' align='end'>
                <ClientDetailsMoreMenu
                  {...props}
                  onDocumentRequestModal={setDocumentRequestModal}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </ButtonGroup>
        }
      >
        <DataColumn>
          <DataCard columns={2} title='Main Information'>
            <FormField name='firstname' label='First Name' readOnly={!isEdit} />
            <FormField name='middlename' label='Middle Name' readOnly={!isEdit} />
            <FormField name='lastname' label='Last Name' readOnly={!isEdit} />
            <FormField name='nativeName' label='Native Name' readOnly={!isEdit} />
            <FormSelectField
              name='gender.name'
              label='Gender'
              readOnly={!isEdit}
              options={gender}
            />
            <FormReadonlyField label='Type' value={values.type.name} />
            <FormDateField
              name='birthday'
              label='Birthday'
              dateFormatType={'dd/MM/yyyy'}
              readOnly={!isEdit}
            />
            <FormReadonlyField label='Username' value={values.username} />
            <FormSelectField
              name='assignee.id'
              label='CFD Assignee'
              allowClear
              readOnly={!isEdit}
              options={assigneeOptions}
              showSearch
            />
            <FormField name='etdAssignee.name' label='ETD Assignee' readOnly />
            <FormFieldLayout label='Products'>
              <div className='flex w-full flex-wrap gap-2'>
                {values.clientProductStates.map((p) => (
                  <Badge
                    variant={
                      p.calculatedStatus.id === ClientStatusId.ACTIVATED ? 'green' : 'orange'
                    }
                    onClick={() => setProductModal(p.tickmillProduct.id)}
                    key={p.tickmillProduct.id}
                  >
                    {p.tickmillProduct.name}
                  </Badge>
                ))}
              </div>
            </FormFieldLayout>
          </DataCard>
          <DataCard columns={2} title='Classification'>
            <FormSelectField
              name='classification.id'
              label='Classification'
              readOnly={!isEdit}
              options={classification}
              onChange={(value) => {
                const nextId = Number.parseInt(String(value), 10)
                const fieldValue = classification.find((x) => x.id === nextId)
                // that checks if classification is poland and then country needs to be set to POL
                if (fieldValue?.id === 6) {
                  if (getPrimary(values.addresses)?.country?.id !== 'POL') {
                    return props.setMessage('Address does not match with your selected option')
                  }
                }
                if (fieldValue) {
                  setFieldValue('classification', fieldValue)
                }
              }}
            />
            <FormSelectField
              name='marketDataClassification.id'
              label='Market Data Classification'
              readOnly={!isEdit}
              options={marketDataClassification}
            />
            <FormCheckboxField
              label='U.S. Citizen'
              name='isUsCitizen'
              readOnly={!isEdit}
              className={
                values.isUsCitizen
                  ? 'rounded-md bg-destructive/10 px-2 py-1 ring-1 ring-destructive/30'
                  : undefined
              }
            />
            <FormCheckboxField
              label='Politically Exposed Person'
              name='isPoliticallyExposedPerson'
              readOnly={!isEdit}
              className={
                values.isPoliticallyExposedPerson
                  ? 'rounded-md bg-destructive/10 px-2 py-1 ring-1 ring-destructive/30'
                  : undefined
              }
            />
          </DataCard>
          <DataCard columns={2} title='Status'>
            <FormSelectField
              name='salesStatus.id'
              label='Sales Status'
              readOnly={!isEdit}
              allowClear
              options={salesStatuses}
              onChange={(value) => {
                const nextId = Number.parseInt(String(value), 10)
                setFieldValue('salesStatus', salesStatuses.find((x) => x.id === nextId) ?? null)
              }}
            />
            <FormSelectField
              name='truliooEIDStatus.id'
              label='EID Status'
              readOnly={!isEdit}
              allowClear
              options={truliooEidStatuses}
              onChange={(value) => {
                const nextId = Number.parseInt(String(value), 10)
                setFieldValue(
                  'truliooEIDStatus',
                  truliooEidStatuses.find((x) => x.id === nextId) ?? null
                )
              }}
            />
            <FormSelectField
              name='truliooDocVStatus.id'
              label='DocV Status'
              readOnly={!isEdit}
              allowClear
              options={truliooDocVStatuses}
              onChange={(value) => {
                const nextId = Number.parseInt(String(value), 10)
                setFieldValue(
                  'truliooDocVStatus',
                  truliooDocVStatuses.find((x) => x.id === nextId) ?? null
                )
              }}
            />
            <FormSelectField
              name='worldCheckStatus.id'
              label='World Check Status'
              readOnly={!isEdit}
              allowClear
              options={worldCheckStatuses}
              onChange={(value) => {
                const nextId = Number.parseInt(String(value), 10)
                setFieldValue(
                  'worldCheckStatus',
                  worldCheckStatuses.find((x) => x.id === nextId) ?? null
                )
              }}
            />
            <FormSelectField
              name='truliooWatchlistStatus.id'
              label='Watchlist'
              readOnly={!isEdit}
              allowClear
              options={truliooWatchlistStatuses}
              onChange={(value) => {
                const nextId = Number.parseInt(String(value), 10)
                setFieldValue(
                  'truliooWatchlistStatus',
                  truliooWatchlistStatuses.find((x) => x.id === nextId) ?? null
                )
              }}
            />
            <FormFieldLayout label='AML Score'>
              {props.data.clientAmlSummary ? (
                <div className='flex items-center gap-2'>
                  <Badge
                    variant='default'
                    onClick={() => {
                      if (hasPermission(permissions, PermissionsEnum.CanViewClientAMLData)) {
                        setAmlDetailsModal(true)
                      }
                    }}
                    style={{
                      background: getAMLScore.color,
                      color: '#FFF',
                    }}
                  >
                    {props.data.clientAmlSummary.score}
                  </Badge>
                  <span>{getAMLScore.name}</span>
                </div>
              ) : (
                <Input readOnly value={values.clientAmlSummary?.score ?? ''} />
              )}
            </FormFieldLayout>
            <FormSelectField
              name='vulnerableGroup.id'
              label='Vulnerable Group'
              readOnly={!isEdit}
              allowClear
              options={complianceVulnerabilityGroups}
              onChange={(value) => {
                const nextId = Number.parseInt(String(value), 10)
                setFieldValue(
                  'vulnerableGroup',
                  complianceVulnerabilityGroups.find((x) => x.id === nextId) ?? null
                )
              }}
            />
          </DataCard>
          <DataCard columns={2} title='Activity'>
            {[
              {
                label: 'Last CA Login',
                value: formatLocalDateUTC(userStatistics?.lastSuccessfulLogin, true),
              },
              {
                label: 'Failed Logins',
                value: userStatistics?.failedLoginCount,
              },
              {
                label: 'Forgot Password Attempts',
                value: userStatistics?.resetPasswordAttempts,
              },
            ].map((item) => (
              <FormReadonlyField key={item.label} label={item.label} value={item.value ?? ''} />
            ))}
            <FormReadonlyField
              label={
                <span className='flex items-center gap-1'>
                  KYC Update Date
                  <Tooltip key='right'>
                    <TooltipTrigger>
                      <Info size={14} />
                    </TooltipTrigger>
                    <TooltipContent side='right' className='flex-col items-start gap-2'>
                      <p>Date on which a client's KYC was last updated in the system.</p>
                      <p>
                        For a client who is less than 1 year old, this date will be the KYC upon
                        registration was recorded in the system.
                      </p>
                      <p>
                        For a client who is more than 1 year old, this date will be the latest date
                        on which their KYC was updated in the system via the KYC update form.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </span>
              }
              value={formatLocalDateUTC(values.detailsLastUpdatedDate, true) ?? ''}
            />
            <FormReadonlyField
              label='Date Created'
              value={formatLocalDateUTC(values.createdDate, true) ?? ''}
            />
            <FormReadonlyField
              label='Date Updated'
              value={formatLocalDateUTC(values.updatedDate, true) ?? ''}
            />
            <FormReadonlyField
              label='Forgot Password Request Date'
              value={formatLocalDateUTC(userStatistics?.forgotPasswordRequestDate, true) ?? ''}
            />
            <FormReadonlyField
              label='Forgot Password Completion Date'
              value={formatLocalDateUTC(userStatistics?.forgotPasswordCompletionDate, true) ?? ''}
            />
          </DataCard>
          <DataCard columns={2} title='More'>
            <FormSelectField
              options={introducingBrokersByCompany}
              label={
                <CacheLabel
                  label='Main IB'
                  message={INTRODUCING_BROKERS_CACHE_MESSAGE}
                  ariaLabel='Introducing Broker cache information'
                />
              }
              showLoading={props.isLoading}
              name='introducingBroker.id'
              readOnly={!isEdit}
              allowClear
              showSearch
              link={
                values.introducingBroker?.id &&
                generatePath(links.recordsIntroducingBrokers.detailsUrl as string, {
                  id: values.introducingBroker?.id,
                  slug: 'details',
                })
              }
            />

            {values.mainIbScheme && (
              <FormReadonlyField
                label='IB Program'
                value={
                  values.mainIbScheme.id === MainIBSchemeEnum.DEFAULT ? 'Default' : 'Multi-Tier'
                }
              />
            )}
            <FormReadonlyField label='Affiliate' value={values.affiliate?.name ?? ''} />
            <FormSelectField
              name='defaultTradingAccountBook.id'
              label='Default Book'
              readOnly={!isEdit}
              options={bookType}
            />
            <FormSelectField
              name='maxLeverage.id'
              readOnly={!isEdit}
              options={leverageOptions}
              label={
                <>
                  Maximum Leverage (MetaTrader)
                  {!!values.restrictions.find((x) => x.id === 4) && (
                    <FormFieldIconBlock>
                      <WarningTwoTone twoToneColor='#ff0000' />
                    </FormFieldIconBlock>
                  )}
                </>
              }
            />
            <FormCheckboxField
              label={
                <>
                  SWAP Free Account
                  {!!values.restrictions.find((x) => x.id === 3) && (
                    <FormFieldIconBlock>
                      <WarningTwoTone twoToneColor='#ff0000' />
                    </FormFieldIconBlock>
                  )}
                </>
              }
              name='isSwapFreeAccount'
              readOnly={!isEdit}
            />
            <FormCheckboxField
              label='Brexit Transfer'
              name='isBrexitTransferAccepted'
              readOnly={!isEdit}
            />
            <FormSelectField
              name='displayCurrency.id'
              label='Display Currency'
              readOnly={!isEdit}
              allowClear
              options={displayCurrencyOptions}
              onChange={(value) =>
                setFieldValue(
                  'displayCurrency',
                  displayCurrency.find((x) => x.id === String(value)) ?? null
                )
              }
            />
            <FormSelectField
              name='preferredCurrency.id'
              label='Preferred Currency'
              readOnly={!isEdit}
              allowClear
              options={preferredCurrencyOptions}
              onChange={(value) =>
                setFieldValue(
                  'preferredCurrency',
                  preferredCurrency.find((x) => x.id === String(value)) ?? null
                )
              }
            />
            <FormSelectField
              name='preferredLandingPage.id'
              label='Landing Page'
              readOnly={!isEdit}
              allowClear
              options={landingPages}
              onChange={(value) =>
                setFieldValue(
                  'preferredLandingPage',
                  landingPages.find((x) => x.id === Number(value)) ?? null
                )
              }
            />
          </DataCard>
        </DataColumn>
        <DataColumn>
          <DataCard columns={2} title='Contact'>
            <FormFieldLayout label='Phone Number'>
              <Button className='pointer-events-none' variant='outline' size='sm'>
                {countryFlag?.map((f) => f)}
              </Button>
              <Select
                placeholder='Country code'
                value={selectPhoneNumber?.countryCode || undefined}
                options={uniqueCountryCodeOptions}
                className='w-full max-w-22'
                showSearch
                readOnly={!isEdit}
                onChange={(value: string) => updatePrimaryPhoneNumber({ countryCode: value })}
              />
              <Input
                className='w-full'
                value={selectPhoneNumber?.number ?? ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  updatePrimaryPhoneNumber({ number: e.target.value })
                }}
                readOnly={!isEdit}
              />
              {selectPhoneNumber?.isVerified && <CheckCheck className='pl-1' color='green' />}
            </FormFieldLayout>
            <FormField
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updatePrimaryEmail(e.target.value)
              }
              value={getPrimary(values.emails)?.address ?? ''}
              name='emailAddress'
              label='Email Address'
              readOnly={!isEdit}
              rightAddon={
                !!values.emails.find((e) => e.isPrimary) && (
                  <CheckCheck className='pl-1' color='green' />
                )
              }
            />
            <FormSelectField
              name='communicationLanguage.id'
              label='Communication Language'
              readOnly={!isEdit}
              options={language}
            />
          </DataCard>
          <DataCard columns={2} title='Address'>
            <FormField
              value={getPrimary(values.addresses)?.street ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updatePrimaryAddress({ street: e.target.value })
              }
              label='Address Line 1'
              readOnly={!isEdit}
            />
            <FormField
              value={getPrimary(values.addresses)?.streetHouseNo ?? 's/n'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updatePrimaryAddress({ streetHouseNo: e.target.value })
              }
              label='Street Nr.'
              readOnly={!isEdit}
            />

            <FormField
              value={getPrimary(values.addresses)?.city ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updatePrimaryAddress({ city: e.target.value })
              }
              label='City'
              readOnly={!isEdit}
            />

            <FormSelectField
              label='Country'
              readOnly={!isEdit}
              value={getPrimary(values.addresses)?.country?.id || undefined}
              canCopy
              showSearch
              onChange={(value) => {
                const fieldValue = countries.find((x) => x.id === value)
                if (fieldValue) {
                  updatePrimaryAddress({ country: fieldValue })
                }
              }}
              options={countryOptions}
            />
            <FormField
              label='State/Region'
              readOnly={!isEdit}
              value={getPrimary(values.addresses)?.state ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updatePrimaryAddress({ state: e.target.value })
              }
            />
            <FormField
              label='Postal Code'
              value={getPrimary(values.addresses)?.postalCode ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updatePrimaryAddress({ postalCode: e.target.value })
              }
              readOnly={!isEdit}
            />
          </DataCard>
          <DataCard columns={2} title='Identification'>
            <FormSelectField
              name='documentType'
              options={documentType}
              showSearch
              label='Document Type'
              readOnly={!isEdit}
            />
            <FormField name='documentId' label='Document ID' readOnly={!isEdit} />
            <FormField name='personalId' label='Personal ID' readOnly={!isEdit} />
            <FormDateField
              name='documentExpirationDate'
              label='Expiry Date'
              dateFormatType={dateFormatType}
              readOnly={!isEdit}
            />
            <FormField
              name='taxId'
              label={
                <span className='flex items-center gap-1'>
                  TIN / GSTN
                  <Tooltip key='right'>
                    <TooltipTrigger>
                      <Info size={14} />
                    </TooltipTrigger>
                    <TooltipContent side='right'>
                      <span>Tax Identification Number / Goods Services Tax Number</span>
                    </TooltipContent>
                  </Tooltip>
                </span>
              }
              readOnly={!isEdit}
            />
            <FormSelectField
              name='taxIdAvailabilityStatus.id'
              label='Tax Availability Status'
              readOnly={!isEdit}
              allowClear
              options={taxIdAvailabilityStatus}
              onChange={(value) => {
                const fieldValue = taxIdAvailabilityStatus.find((x) => {
                  if (typeof x.id === 'string') {
                    return x.id === String(value)
                  }
                  return x.id === Number.parseInt(String(value), 10)
                })
                setFieldValue('taxIdAvailabilityStatus', fieldValue ?? null)
              }}
            />
            <FormSelectField
              name='nciType.id'
              label='NCI'
              readOnly={!isEdit}
              options={nciType}
              onChange={(value) => {
                const nextId = Number.parseInt(String(value), 10)
                setFieldValue('nciType', nciType.find((x) => x.id === nextId) ?? null)
              }}
            />
            <FormField
              name='nationalClientIdentifier'
              label='National Client Identifier'
              readOnly={!isEdit}
            />
          </DataCard>
          <DataCard title='Education'>
            <FormSelectField
              name='educationLevel.id'
              label='Education'
              readOnly={!isEdit}
              allowClear
              options={education}
              onChange={(value) => {
                const nextId = Number.parseInt(String(value), 10)
                setFieldValue('educationLevel', education.find((x) => x.id === nextId) ?? null)
              }}
            />
          </DataCard>
          <DataCard columns={2} title='Employment'>
            <FormCheckboxField label='Employed' name='isEmployed' readOnly={!isEdit} />
            <FormField name='employer' label='Employer Name' readOnly={!isEdit} />
            <FormSelectField
              name='businessNature.id'
              label='Nature of Business'
              readOnly={!isEdit}
              allowClear
              showSearch
              options={businessNature}
              onChange={(value) => {
                const fieldValue = businessNature.find((x) => x.id === Number(value))
                setFieldValue('businessNature', fieldValue ?? null)
              }}
            />
          </DataCard>
          <DataCard title='Nationalities'>
            <Field>
              <AddButton
                text='Add Nationality'
                disabled={!isEdit}
                onClick={() =>
                  setModalData({
                    type: 'nationality',
                    data: { name: '', isPrimary: false, id: '' },
                  })
                }
              />
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nationality</TableHead>
                    <TableHead className='w-24'>Primary</TableHead>
                    <TableHead className='w-16' />
                  </TableRow>
                </TableHeader>
                <TableBody>{renderNationalities()}</TableBody>
              </Table>
            </Field>
          </DataCard>
          <DataCard title='Teams'>
            <Field>
              <AddButton
                text='Add Team'
                disabled={!isEdit}
                onClick={() =>
                  setModalData({
                    type: 'teams',
                    data: { name: '', isPrimary: false, id: '' },
                  })
                }
              />
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Team Name</TableHead>
                    <TableHead className='w-24'>Primary</TableHead>
                    <TableHead className='w-16' />
                  </TableRow>
                </TableHeader>
                <TableBody>{renderTeams()}</TableBody>
              </Table>
            </Field>
          </DataCard>
          <DataCard title='Account Platform Limits'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Platform</TableHead>
                  <TableHead>Live Accounts Limit</TableHead>
                  <TableHead>Demo Accounts Limit</TableHead>
                  <TableHead>TV Plan</TableHead>
                  <TableHead className='w-16' />
                </TableRow>
              </TableHeader>
              <TableBody>{renderPlatformLimits()}</TableBody>
            </Table>
          </DataCard>
        </DataColumn>
      </DataGrid>
    </React.Fragment>
  )
}

const ProductDetails: React.FC<{ productState?: ClientProductStatesDto }> = ({ productState }) => (
  <>
    <div className='grid gap-4 md:grid-cols-2'>
      <FormReadonlyField label='Product' value={productState?.tickmillProduct.name} />
      <FormReadonlyField label='Status' value={productState?.calculatedStatus.name} />
    </div>
    <div className='mt-4 grid gap-3'>
      <FormCheckboxField
        label='Appropriateness Test Submitted'
        value={productState?.submittedAppropriatenessTest}
        readOnly
      />
      <FormCheckboxField
        label='Appropriateness Test Passed'
        value={productState?.passedAppropriatenessTest}
        readOnly
      />
      <FormCheckboxField
        label='Agreed to Legal Documents'
        value={productState?.agreedToLegalDocuments}
        readOnly
      />
      <FormCheckboxField
        label='Set Market Data Classification'
        value={productState?.setMarketDataClassification}
        readOnly
      />
      <FormCheckboxField
        label='Appropriateness Test MaximumRetake Limit Reached'
        value={productState?.appropriatenessTestMaximumRetakeLimitReached}
        readOnly
      />
    </div>
  </>
)

export interface ClientDetailsDashboardFormProps {
  show2FA: boolean
  showCreateLead: boolean
  data: ClientDetailedDto
  userStatistics?: UserStatisticsDto
  tickmillProducts: TickmillProductDto[]
  amlCategories: NameDto[]
  permissions: PermissionDto[]
  clientType: ClientType[]
  gender: NameDto[]
  nciType: NciType[]
  education: EduLevel[]
  teams: TeamDto[]
  documentType: NameDto[]
  businessNature: BusinessNature[]
  tickmillCompanies: TickmillCompany[]
  language: Language[]
  classification: NameDto[]
  marketDataClassification: NameDto[]
  bookType: BookType[]
  accountLimits: ClientPlatformLimitDto[]
  countries: CountryDto[]
  users: SimpleUserResponse[]
  truliooEidStatuses: TruliooStatus[]
  truliooWatchlistStatuses: TruliooStatus[]
  truliooDocVStatuses: TruliooStatus[]
  worldCheckStatuses: WorldCheckStatus[]
  salesStatuses: SalesStatus[]
  preferredCurrency: { id: string; name: string }[]
  displayCurrency: { id: string; name: string }[]
  landingPages: LandingPageDto[]
  introducingBrokersByCompany: IntroducingBrokerBasicDto[]
  complianceVulnerabilityGroups: NameDto[]
  dateFormatType?: string
  leverageType: LeverageType[]
  tradingViewPlans: TradingViewPlanDto[]
  tradingViewPlanExpirationDays: TradingViewPlanExpirationDaysDto[]
  taxIdAvailabilityStatus: NameDto[]
  isLoading: boolean

  onSubmit(values: ClientDetailedDto): void

  onWorldCheckResolve(): void

  getIntroducingBrokersByTickmillCompany(): void

  resetFailedLogins(): void

  resetForgotPasswordAttempts(): void

  updatePlatformLimits: (platformsData: UpdateClientPlatformLimitDto) => void

  updateClientStatus(values: UpdateClientStatus): void

  setWorldCheckModal({
    visible,
    enableOngoingScreening,
  }: {
    visible: boolean
    enableOngoingScreening: boolean
  }): void

  setMessage(msg: string): void

  setDocumentPreviewModal(value: string | null): void

  setAccessLogModal(value: boolean): void
  setAmlDetailsModal(value: boolean): void

  setAuditLogModal(value: boolean): void
  setTwoFactorModal(): void
  createLead(): void

  verifyClient(values: UpdateClientStatus): void

  verifyClientModal(): void

  handleIdentityVerification(): void

  setVerificationModal(value: boolean): void

  setWorldCheckHistoryModal(value: boolean): void

  setNCIHistoryModal({ visible, showAddForm }: { visible: boolean; showAddForm?: boolean }): void

  reCalculateNCI(): void

  syncHubspot(): void

  sendClientRequest(type: POIDocumentType): void

  sendClientOtherRequest(type: DocumentRequestType): void

  assignAffiliate(): void
  showForceLogoutModalConfirmation(): void
  onCALogin(): void
}

type ClientDetailsFormValues = Omit<
  ClientDetailedDto,
  'nativeName' | 'salesStatus' | 'educationLevel'
> & {
  nativeName: string
  salesStatus: SalesStatus | null
  educationLevel: EduLevel | null
  vulnerableGroup?: NameDto | null
  taxIdAvailabilityStatus?: NameDto | null
}

export const ClientDetailsDashboardForm = withFormik<
  ClientDetailsDashboardFormProps,
  ClientDetailsFormValues
>({
  mapPropsToValues: (props) => {
    const data = props.data as ClientDetailsFormValues
    return {
      ...data,
      nativeName: data.nativeName || '',
      middlename: data.middlename || '',
      taxId: data.taxId || '',
      personalId: data.personalId || '',
      documentId: data.documentId || '',
      employer: data.employer || '',
      nationalClientIdentifier: data.nationalClientIdentifier || '',
      salesStatus: data.salesStatus || null,
      educationLevel: data.educationLevel || null,
      vulnerableGroup: data.vulnerableGroup || null,
      taxIdAvailabilityStatus: data.taxIdAvailabilityStatus || null,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    await props.onSubmit(values as ClientDetailedDto)
    setSubmitting(false)
  },
  enableReinitialize: true,
})(ClientDetailsDashboardFormUI)
